import { registerTriggerSource } from '../registry.js';
import type {
  TriggerEvent,
  TriggerPollContext,
  TriggerPollResult,
  TriggerSourceAdapter,
  TriggerSubscription,
} from '../types.js';

/**
 * Google Doc comment watcher — the first poll-kind trigger (ENG-5993, S2 of
 * the Triggers epic). Polls Drive v3 comments.list with a modifiedTime
 * watermark and emits an event per @-mention of the agent.
 *
 * Why poll: Google offers NO push for Doc comments (changes.watch is
 * file-scoped and comments never enter the changes feed; the Activity API is
 * query-only; the @-mention email is batched ~10 min). Full findings:
 * docs/spikes/eng-5986-poll-drive-comments.md Parts A/B.
 *
 * Purity: the adapter never talks HTTP itself — the executor injects a
 * `listComments` fetcher via ctx.credentials (Composio
 * GOOGLEDRIVE_LIST_COMMENTS server-side, with client-side modifiedTime
 * filtering as the fallback when startModifiedTime isn't passed through).
 * Everything testable lives here: watermark semantics, thread classification,
 * the mention filter, dedup keys, cursor advancement.
 */

// --------------------------------------------------------------------------
// Wire shapes (Drive v3 Comment / Reply, the fields selector subset)
// --------------------------------------------------------------------------

export interface DriveReply {
  id?: string;
  createdTime?: string;
  modifiedTime?: string;
  action?: string; // 'resolve' | 'reopen' | absent for ordinary replies
  deleted?: boolean;
  author?: { displayName?: string };
  content?: string;
}

export interface DriveComment {
  id?: string;
  createdTime?: string;
  modifiedTime?: string; // bumps when the comment OR ANY REPLY changes
  resolved?: boolean;
  deleted?: boolean;
  author?: { displayName?: string };
  content?: string;
  quotedFileContent?: { value?: string };
  replies?: DriveReply[];
}

export interface DriveCommentListPage {
  comments?: DriveComment[];
  nextPageToken?: string;
}

/**
 * Injected by the executor. `startModifiedTime` is best-effort: if the
 * underlying transport (Composio action) can't pass it through, the fetcher
 * may return unfiltered pages — the adapter re-filters client-side either
 * way, so correctness never depends on server-side filtering.
 */
export interface DriveCommentsFetcher {
  listComments(params: {
    fileId: string;
    startModifiedTime?: string;
    pageToken?: string;
  }): Promise<DriveCommentListPage>;
}

export interface GdriveCommentsTriggerConfig {
  /** Drive file id of the watched Doc. */
  fileId: string;
  /**
   * Strings identifying the agent in comment text (display name and/or the
   * mentionable Google identity's email). A comment/reply is delivered only
   * when its content contains one of these (case-insensitive).
   */
  mention: string[];
}

/** Cursor persisted in trigger_poll_state.cursor — the spike's watermark. */
interface GdriveCommentsCursor {
  modifiedTime?: string;
}

// --------------------------------------------------------------------------
// Classification (spike doc Part A, Q2 — the delta-semantics table)
// --------------------------------------------------------------------------

export type GdriveCommentEventKind = 'NEW_COMMENT' | 'NEW_REPLY';

interface ClassifiedMention {
  kind: GdriveCommentEventKind;
  comment: DriveComment;
  /** Set for NEW_REPLY — the reply that fired. */
  reply?: DriveReply;
}

function containsMention(text: string | undefined, mentions: string[]): boolean {
  if (!text) return false;
  const lower = text.toLowerCase();
  return mentions.some((m) => m.length > 0 && lower.includes(m.toLowerCase()));
}

/**
 * Given the threads whose modifiedTime moved past the watermark, pick out the
 * sub-events the agent should react to: new comments and new replies that
 * @-mention it. Edits/resolves/reopens are deliberately NOT delivered in v1
 * (the agent reacts to being summoned, and RESOLVED is the signal to stop —
 * which dedup handles naturally since resolved threads stop producing new
 * mention events). Resolved threads are skipped outright: replying to a
 * resolved thread is the uncanny trust-killer the council flagged.
 */
export function classifyMentions(
  comments: DriveComment[],
  watermark: string | undefined,
  mentions: string[],
): ClassifiedMention[] {
  const watermarkMs = watermark ? Date.parse(watermark) : Number.NEGATIVE_INFINITY;
  const out: ClassifiedMention[] = [];

  for (const comment of comments) {
    if (!comment.id || comment.deleted) continue;
    // No reply to resolved threads — ever (AC2).
    if (comment.resolved) continue;

    const createdMs = comment.createdTime ? Date.parse(comment.createdTime) : Number.NaN;
    if (Number.isFinite(createdMs) && createdMs >= watermarkMs) {
      // Brand-new comment thread.
      if (containsMention(comment.content, mentions)) {
        out.push({ kind: 'NEW_COMMENT', comment });
      }
    }

    for (const reply of comment.replies ?? []) {
      if (!reply.id || reply.deleted) continue;
      if (reply.action) continue; // resolve/reopen events — not mentions
      const replyCreatedMs = reply.createdTime ? Date.parse(reply.createdTime) : Number.NaN;
      if (!Number.isFinite(replyCreatedMs) || replyCreatedMs < watermarkMs) continue;
      if (!containsMention(reply.content, mentions)) continue;
      out.push({ kind: 'NEW_REPLY', comment, reply });
    }
  }

  return out;
}

/** Next watermark = max(modifiedTime) across every thread the page returned. */
export function nextWatermark(
  comments: DriveComment[],
  current: string | undefined,
): string | undefined {
  let max = current;
  for (const c of comments) {
    if (c.modifiedTime && (!max || c.modifiedTime > max)) max = c.modifiedTime;
  }
  return max;
}

function renderMentionBody(m: ClassifiedMention, fileId: string): string {
  const docUrl = `https://docs.google.com/document/d/${fileId}/edit`;
  const quoted = m.comment.quotedFileContent?.value
    ? `\n> ${m.comment.quotedFileContent.value}`
    : '';
  if (m.kind === 'NEW_REPLY' && m.reply) {
    return (
      `${m.reply.author?.displayName ?? 'Someone'} replied in a comment thread on a Google Doc you watch and mentioned you:` +
      `\n\n${m.reply.content ?? ''}` +
      `\n\nThread opener (${m.comment.author?.displayName ?? 'unknown'}): ${m.comment.content ?? ''}${quoted}` +
      `\n\nDoc: ${docUrl} (comment id ${m.comment.id})`
    );
  }
  return (
    `${m.comment.author?.displayName ?? 'Someone'} mentioned you in a new comment on a Google Doc you watch:` +
    `\n\n${m.comment.content ?? ''}${quoted}` +
    `\n\nDoc: ${docUrl} (comment id ${m.comment.id})`
  );
}

// --------------------------------------------------------------------------
// The adapter
// --------------------------------------------------------------------------

export const gdriveCommentsTriggerAdapter: TriggerSourceAdapter = {
  provider: 'gdrive_comments',
  kind: 'poll',

  async poll(ctx: TriggerPollContext, trigger: TriggerSubscription): Promise<TriggerPollResult> {
    const config = trigger.config as unknown as Partial<GdriveCommentsTriggerConfig>;
    const fileId = typeof config.fileId === 'string' ? config.fileId : undefined;
    const mentions = Array.isArray(config.mention)
      ? config.mention.filter((m): m is string => typeof m === 'string' && m.length > 0)
      : [];
    if (!fileId || mentions.length === 0) {
      throw new Error('gdrive_comments: trigger config requires fileId and mention[]');
    }

    const fetcher = ctx.credentials as DriveCommentsFetcher | undefined;
    if (!fetcher || typeof fetcher.listComments !== 'function') {
      throw new Error('gdrive_comments: executor must inject a DriveCommentsFetcher');
    }

    const cursor = (ctx.cursor ?? {}) as GdriveCommentsCursor;
    const watermark = typeof cursor.modifiedTime === 'string' ? cursor.modifiedTime : undefined;

    // Page through everything past the watermark. startModifiedTime is
    // inclusive (>=) when honoured server-side; the client-side re-filter
    // below makes the unfiltered (Composio-fallback) case identical.
    const threads: DriveComment[] = [];
    let pageToken: string | undefined;
    do {
      const page = await fetcher.listComments({
        fileId,
        ...(watermark ? { startModifiedTime: watermark } : {}),
        ...(pageToken ? { pageToken } : {}),
      });
      for (const c of page.comments ?? []) {
        // Client-side watermark re-filter — correctness never depends on the
        // transport honouring startModifiedTime ([verify live] fallback).
        if (watermark && c.modifiedTime && c.modifiedTime < watermark) continue;
        threads.push(c);
      }
      pageToken = page.nextPageToken;
    } while (pageToken);

    const events: TriggerEvent[] = classifyMentions(threads, watermark, mentions).map((m) => {
      const sourceId =
        m.kind === 'NEW_REPLY' && m.reply
          ? `${m.comment.id}:${m.reply.id}:${m.reply.createdTime ?? ''}`
          : `${m.comment.id}:${m.comment.createdTime ?? ''}`;
      return {
        provider: 'gdrive_comments',
        occurredAt:
          (m.kind === 'NEW_REPLY' ? m.reply?.createdTime : m.comment.createdTime) ??
          new Date().toISOString(),
        // Keyed on the IMMUTABLE creation identity of the mention (not the
        // thread's rolling modifiedTime), so an unrelated later edit to the
        // same thread can never re-deliver the mention (AC2) and overlapping
        // polls / restarts collapse onto one row (AC3).
        dedupKey: `gdc:${sourceId}`,
        sourceTrust: 'untrusted',
        title: m.kind === 'NEW_COMMENT' ? 'New comment mention' : 'New reply mention',
        body: renderMentionBody(m, fileId),
        raw: m.kind === 'NEW_REPLY' ? { comment: m.comment, reply: m.reply } : { comment: m.comment },
        meaningful: true,
        // ENG-6071: ask the executor to drop a short ack reply in the
        // thread the moment this event is first recorded (the Slack-eyes
        // analogue). Loop-safe: the executor's phrase picker excludes
        // anything containing a mention string (so classifyMentions never
        // emits an event for the ack) and dedup keys are immutable
        // creation identities — the bumped thread modifiedTime just causes
        // one harmless re-read.
        //
        // ENG-6081: threadDedupPrefix groups every event in this thread —
        // `gdc:<commentId>:<createdTime>` (NEW_COMMENT) and
        // `gdc:<commentId>:<replyId>:<createdTime>` (NEW_REPLY) both share
        // it, so the executor acks only the FIRST engagement per thread.
        ...(m.comment.id
          ? {
              ack: {
                kind: 'gdrive_comment_reply' as const,
                fileId,
                commentId: m.comment.id,
                threadDedupPrefix: `gdc:${m.comment.id}:`,
              },
            }
          : {}),
      };
    });

    // Process-then-commit: the executor persists this cursor only after the
    // events are durably recorded; the inclusive boundary + immutable dedup
    // keys make the overlap re-scan harmless.
    return { events, cursor: { modifiedTime: nextWatermark(threads, watermark) } };
  },
};

registerTriggerSource(gdriveCommentsTriggerAdapter);
